import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './use-auth'
import { tidyPhone } from '@/lib/ph'

export interface Client {
  id: string
  first_name: string | null
  last_name: string | null
  organization: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  address: string | null
  line1: string | null
  line2: string | null
  latitude: number | null
  longitude: number | null
  manual_address: boolean | null
  city: string | null
  state: string | null
  postal_code: string | null
  contact_type: string | null
  status: string | null
  tags: string[] | null
  notes: string | null
  last_contacted_at: string | null
  company_id: string
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
}

export interface ClientActivity {
  id: string
  contact_id: string
  user_id: string | null
  type: string
  body: string | null
  created_at: string | null
}

export function clientName(c: Pick<Client, 'first_name' | 'last_name' | 'organization'>) {
  const person = [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
  return person || c.organization || 'Unnamed client'
}

export function useClients(clientId?: string) {
  const { companyId, user } = useAuth()
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['clients'],
    queryFn: async (): Promise<Client[]> => {
      // PostgREST caps an unbounded request at 1,000 rows. There was no .limit() here, so this
      // LOOKED unlimited while silently truncating: a customer has 1,615 clients, so 615 of them were
      // missing from the Clients page and from every client dropdown, with no error to notice.
      // Verified against the live API — the response came back `content-range: 0-999/1726`.
      const PAGE = 1000
      const all: Client[] = []
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from('contact')
          .select('*')
          .is('deleted_at', null)
          .order('first_name', { ascending: true })
          .order('id', { ascending: true })   // tie-break, or a repeated first_name can straddle pages
          .range(from, from + PAGE - 1)
        if (error) throw error
        const batch = (data ?? []) as Client[]
        all.push(...batch)
        if (batch.length < PAGE) break
      }
      return all
    },
  })

  const detail = useQuery({
    queryKey: ['client', clientId],
    queryFn: async (): Promise<Client | null> => {
      const { data, error } = await supabase.from('contact').select('*').eq('id', clientId!).maybeSingle()
      if (error) throw error
      return data as Client | null
    },
    enabled: !!clientId,
  })

  const activity = useQuery({
    queryKey: ['client-activity', clientId],
    queryFn: async (): Promise<ClientActivity[]> => {
      const { data, error } = await supabase
        .from('contact_activity')
        .select('*')
        .eq('contact_id', clientId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as ClientActivity[]
    },
    enabled: !!clientId,
  })

  const create = useMutation({
    mutationFn: async (payload: Partial<Client>): Promise<Client> => {
      const { data, error } = await supabase
        .from('contact')
        .insert({ ...payload, ...tidyPhones(payload), company_id: companyId, user_id: user!.id })
        .select()
        .single()
      if (error) throw error
      return data as Client
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  // CSV import with dedupe on email/phone (ONB-2). Valid rows succeed even if
  // some fail; returns a per-batch report.
  const importContacts = useMutation({
    mutationFn: async (rows: Array<Partial<Client>>): Promise<{ inserted: number; skipped: number; failed: number }> => {
      const existing = await supabase.from('contact').select('email, phone, mobile').is('deleted_at', null)
      const seen = new Set<string>()
      ;(existing.data ?? []).forEach((c) => {
        const r = c as { email: string | null; phone: string | null; mobile: string | null }
        ;[r.email, r.phone, r.mobile].forEach((v) => v && seen.add(v.trim().toLowerCase()))
      })
      let skipped = 0
      const toInsert: Array<Record<string, unknown>> = []
      for (const row of rows) {
        const keys = [row.email, tidyPhone(row.phone)].filter(Boolean).map((v) => String(v).trim().toLowerCase())
        if (keys.some((k) => seen.has(k))) { skipped++; continue }
        keys.forEach((k) => seen.add(k))
        toInsert.push({
          company_id: companyId, user_id: user!.id,
          first_name: row.first_name ?? null, last_name: row.last_name ?? null,
          organization: row.organization ?? null, email: row.email ?? null,
          phone: tidyPhone(row.phone), mobile: tidyPhone(row.mobile), address: row.address ?? null,
        })
      }
      let failed = 0
      if (toInsert.length) {
        const { error } = await supabase.from('contact').insert(toInsert)
        if (error) failed = toInsert.length
      }
      return { inserted: failed ? 0 : toInsert.length, skipped, failed }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...patch }: Partial<Client> & { id: string }): Promise<Client> => {
      const { data, error } = await supabase
        .from('contact')
        .update({ ...patch, ...tidyPhones(patch), updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data as Client
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['client', c.id] })
    },
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('contact').update({ deleted_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const addActivity = useMutation({
    mutationFn: async (input: { contact_id: string; type?: string; body: string }) => {
      const { data, error } = await supabase
        .from('contact_activity')
        .insert({
          company_id: companyId,
          contact_id: input.contact_id,
          user_id: user!.id,
          type: input.type ?? 'note',
          body: input.body,
        })
        .select()
        .single()
      if (error) throw error
      return data as ClientActivity
    },
    onSuccess: (a) => qc.invalidateQueries({ queryKey: ['client-activity', a.contact_id] }),
  })

  return {
    clients: list.data ?? [],
    isLoading: list.isLoading,
    client: detail.data ?? null,
    isLoadingDetail: detail.isLoading,
    activity: activity.data ?? [],
    create,
    importContacts,
    update,
    remove,
    addActivity,
  }
}

/** Spec 001: Philippine mobiles are stored as +639XXXXXXXXX. Only the fields present are touched. */
function tidyPhones(p: { phone?: string | null; mobile?: string | null }): { phone?: string | null; mobile?: string | null } {
  const out: { phone?: string | null; mobile?: string | null } = {}
  if ('phone' in p) out.phone = tidyPhone(p.phone)
  if ('mobile' in p) out.mobile = tidyPhone(p.mobile)
  return out
}
